import { Frame } from '@/types/event';

interface FrameTimeIndex {
    frames: Frame[];
    anchor: Map<number, number>;
    intervalSeconds: number;
}

function intervalKey(time: number, intervalSeconds: number): number {
    return Math.floor(time / intervalSeconds);
}

export function buildIndex(
    samples: Frame[],
    intervalSeconds: number = 0.25,
): FrameTimeIndex {
    const sorted = [...samples].sort((a, b) => a.time - b.time);

    const anchor = new Map<number, number>();
    for (let index = 0; index < sorted.length; index++) {
        anchor.set(intervalKey(sorted[index].time, intervalSeconds), index);
    }

    return {
        frames: sorted,
        anchor,
        intervalSeconds,
    };
}

export function makeGetSurroundingFrames(index: FrameTimeIndex) {
    const { frames, anchor, intervalSeconds } = index;
    let currentIndex = 0;

    return function getSurroundingFrames(time: number): {
        current: Frame | null;
        next: Frame | null;
    } {
        if (frames.length === 0) return { current: null, next: null };

        if (
            currentIndex >= frames.length ||
            Math.abs(frames[currentIndex].time - time) > intervalSeconds * 2
        ) {
            const key = intervalKey(time, intervalSeconds);
            const anchorIndex = anchor.get(key) ?? anchor.get(key - 1) ?? 0;
            currentIndex = anchorIndex;
        }

        while (
            currentIndex < frames.length - 1 &&
            frames[currentIndex + 1].time <= time
        ) {
            currentIndex++;
        }

        while (currentIndex > 0 && frames[currentIndex].time > time) {
            currentIndex--;
        }

        const current = frames[currentIndex];
        const next = frames[currentIndex + 1] ?? current;

        return { current, next };
    };
}
